/**
 * Mouse Tracking for Analytics Heatmaps
 * Tracks mouse movements, clicks, and scroll events for heatmap generation
 * Also records session replay data for detailed behavior analysis
 */

class MouseTracker {
    constructor(options = {}) {
        this.pageUrl = options.pageUrl || window.location.pathname;
        this.sessionId = options.sessionId || this.getSessionId();
        this.endpoint = options.endpoint || '/api/analytics/track-mouse';
        this.sessionEndpoint = options.sessionEndpoint || '/api/analytics/record-session';
        this.throttleDelay = options.throttleDelay || 500; // Increased from 100ms to 500ms
        this.batchSize = options.batchSize || 50; // Increased from 10 to 50
        this.sessionBatchSize = options.sessionBatchSize || 100; // Increased from 50 to 100
        this.sendInterval = options.sendInterval || 30000; // 30 seconds instead of 5
        this.sessionSendInterval = options.sessionSendInterval || 15000; // 15 seconds instead of 3
        this.enabled = options.enabled !== false;
        this.recordSession = options.recordSession !== false;

        this.events = [];
        this.sessionEvents = [];
        this.sequenceNumber = 0;
        this.lastMouseMove = 0;
        this.viewportWidth = window.innerWidth;
        this.viewportHeight = window.innerHeight;
        this.scrollX = window.scrollX;
        this.scrollY = window.scrollY;

        // Mouse movement compression
        this.mouseMovements = [];
        this.lastMovementTime = 0;
        this.movementBatchSize = 20; // Collect 20 movements before compressing

        // Load cached events from localStorage
        this.loadCachedEvents();

        if (this.enabled) {
            this.init();
        }
    }

    init() {
        // Record pageview event for session replay
        if (this.recordSession) {
            this.recordSessionEvent('pageview', {
                url: window.location.href,
                referrer: document.referrer,
                userAgent: navigator.userAgent,
                viewport: { width: this.viewportWidth, height: this.viewportHeight },
                screen: { width: screen.width, height: screen.height }
            });
        }

        // Track mouse movements
        document.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: true });

        // Track clicks
        document.addEventListener('click', this.handleClick.bind(this), { passive: true });

        // Track scroll events
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        // Track viewport changes
        window.addEventListener('resize', this.handleResize.bind(this), { passive: true });

        // Track input changes (for forms)
        document.addEventListener('input', this.handleInput.bind(this), { passive: true });

        // Send data periodically with new intervals
        this.sendIntervalId = setInterval(() => this.sendBatch(), this.sendInterval);
        this.sessionSendIntervalId = setInterval(() => this.sendSessionBatch(), this.sessionSendInterval);

        // Send remaining data before page unload
        window.addEventListener('beforeunload', () => {
            this.recordSessionEvent('unload', { duration: Date.now() - this.pageStartTime });
            this.sendMouseMovementBatch(); // Send any remaining mouse movements
            this.saveCachedEvents();
            this.sendBatch(true);
            this.sendSessionBatch(true);
        });

        // Save cached events periodically
        setInterval(() => this.saveCachedEvents(), 10000); // Save every 10 seconds

        this.pageStartTime = Date.now();
    }

    getSessionId() {
        let sessionId = localStorage.getItem('analytics_session_id');
        if (!sessionId) {
            sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('analytics_session_id', sessionId);
        }
        return sessionId;
    }

    loadCachedEvents() {
        try {
            const cachedEvents = localStorage.getItem(`analytics_events_${this.sessionId}`);
            const cachedSessionEvents = localStorage.getItem(`analytics_session_events_${this.sessionId}`);
            const cachedMouseMovements = localStorage.getItem(`analytics_mouse_movements_${this.sessionId}`);

            if (cachedEvents) {
                const parsed = JSON.parse(cachedEvents);
                this.events = parsed.filter(event => {
                    // Only keep events from the last hour
                    const eventTime = new Date(event.timestamp).getTime();
                    return Date.now() - eventTime < 3600000;
                });
            }

            if (cachedSessionEvents) {
                const parsed = JSON.parse(cachedSessionEvents);
                this.sessionEvents = parsed.filter(event => {
                    // Only keep events from the last hour
                    const eventTime = new Date(event.timestamp).getTime();
                    return Date.now() - eventTime < 3600000;
                });
                // Update sequence number
                this.sequenceNumber = Math.max(...this.sessionEvents.map(e => e.sequence_number || 0), 0);
            }

            if (cachedMouseMovements) {
                const parsed = JSON.parse(cachedMouseMovements);
                // Only keep movements from the last 30 minutes
                this.mouseMovements = parsed.filter(movement => {
                    return Date.now() - movement.time < 1800000;
                });
            }
        } catch (error) {
            console.warn('Failed to load cached analytics events:', error);
        }
    }

    saveCachedEvents() {
        try {
            if (this.events.length > 0) {
                localStorage.setItem(`analytics_events_${this.sessionId}`, JSON.stringify(this.events));
            }
            if (this.sessionEvents.length > 0) {
                localStorage.setItem(`analytics_session_events_${this.sessionId}`, JSON.stringify(this.sessionEvents));
            }
            if (this.mouseMovements.length > 0) {
                localStorage.setItem(`analytics_mouse_movements_${this.sessionId}`, JSON.stringify(this.mouseMovements));
            }
        } catch (error) {
            console.warn('Failed to save cached analytics events:', error);
            // If localStorage is full, clear old events
            if (error.name === 'QuotaExceededError') {
                this.events = this.events.slice(-10); // Keep only last 10 events
                this.sessionEvents = this.sessionEvents.slice(-20); // Keep only last 20 session events
                this.mouseMovements = this.mouseMovements.slice(-10); // Keep only last 10 movements
            }
        }
    }

    handleMouseMove(event) {
        const now = Date.now();
        if (now - this.lastMouseMove < this.throttleDelay) return;

        this.lastMouseMove = now;

        // Collect mouse movement data
        this.mouseMovements.push({
            x: event.clientX,
            y: event.clientY,
            time: now
        });

        // Record for session replay (keep individual events for replay)
        if (this.recordSession) {
            this.recordSessionEvent('mousemove', {
                x: event.clientX,
                y: event.clientY,
                scrollX: this.scrollX,
                scrollY: this.scrollY,
                viewportWidth: this.viewportWidth,
                viewportHeight: this.viewportHeight
            });
        }

        // Send compressed batch when we have enough movements
        if (this.mouseMovements.length >= this.movementBatchSize) {
            this.sendMouseMovementBatch();
        }
    }

    handleClick(event) {
        const target = event.target;
        const rect = target.getBoundingClientRect();

        const eventData = {
            session_id: this.sessionId,
            page_url: this.pageUrl,
            event_type: 'click',
            x_position: Math.round(event.clientX),
            y_position: Math.round(event.clientY),
            viewport_width: this.viewportWidth,
            viewport_height: this.viewportHeight,
            scroll_x: this.scrollX,
            scroll_y: this.scrollY,
            element_tag: target.tagName.toLowerCase(),
            element_id: target.id || null,
            element_class: target.className || null,
            timestamp: new Date().toISOString()
        };

        this.events.push(eventData);
        this.sendBatch(); // Send clicks immediately

        // Record for session replay
        if (this.recordSession) {
            this.recordSessionEvent('click', {
                x: Math.round(event.clientX),
                y: Math.round(event.clientY),
                target: {
                    tag: target.tagName.toLowerCase(),
                    id: target.id,
                    className: target.className,
                    text: target.textContent?.substring(0, 100) || '',
                    href: target.href || null
                },
                scrollX: this.scrollX,
                scrollY: this.scrollY
            });
        }
    }

    sendMouseMovementBatch() {
        if (this.mouseMovements.length === 0) return;

        const movements = [...this.mouseMovements];
        this.mouseMovements = [];

        const compressedEvent = {
            session_id: this.sessionId,
            page_url: this.pageUrl,
            event_type: 'mousemove_batch',
            viewport_width: this.viewportWidth,
            viewport_height: this.viewportHeight,
            scroll_x: this.scrollX,
            scroll_y: this.scrollY,
            movements: movements,
            timestamp: new Date().toISOString()
        };

        this.events.push(compressedEvent);

        if (this.events.length >= this.batchSize) {
            this.sendBatch();
        }
    }

    handleInput(event) {
        const target = event.target;

        // Only track certain input types and avoid password fields
        if (target.type === 'password' || target.type === 'hidden') return;

        if (this.recordSession) {
            this.recordSessionEvent('input', {
                target: {
                    tag: target.tagName.toLowerCase(),
                    id: target.id,
                    name: target.name,
                    type: target.type
                },
                value: target.value?.length || 0 // Don't store actual values for privacy
            });
        }
    }

    recordSessionEvent(eventType, eventData) {
        this.sequenceNumber++;

        const sessionEvent = {
            session_id: this.sessionId,
            page_url: this.pageUrl,
            event_type: eventType,
            event_data: eventData,
            sequence_number: this.sequenceNumber,
            timestamp: new Date().toISOString()
        };

        this.sessionEvents.push(sessionEvent);

        if (this.sessionEvents.length >= this.sessionBatchSize) {
            this.sendSessionBatch();
        }
    }

    async sendSessionBatch(force = false) {
        if (this.sessionEvents.length === 0) return;

        const batch = force ? [...this.sessionEvents] : this.sessionEvents.splice(0, this.sessionBatchSize);

        try {
            const response = await fetch(this.sessionEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ events: batch }),
                keepalive: force
            });

            if (response.ok) {
                // Clear cached events on successful send
                this.saveCachedEvents();
            } else {
                console.warn('Failed to send session replay data:', response.status);
                if (!force) {
                    this.sessionEvents.unshift(...batch);
                }
            }
        } catch (error) {
            console.warn('Session replay error:', error);
            if (!force) {
                this.sessionEvents.unshift(...batch);
            }
        }
    }

    handleScroll() {
        this.scrollX = window.scrollX;
        this.scrollY = window.scrollY;
    }

    handleResize() {
        this.viewportWidth = window.innerWidth;
        this.viewportHeight = window.innerHeight;
    }

    async sendBatch(force = false) {
        if (this.events.length === 0) return;

        const batch = force ? [...this.events] : this.events.splice(0, this.batchSize);

        try {
            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ events: batch }),
                keepalive: force // Keep connection alive for page unload
            });

            if (response.ok) {
                // Clear cached events on successful send
                this.saveCachedEvents();
            } else {
                console.warn('Failed to send mouse tracking data:', response.status);
                // Re-queue events if sending failed
                if (!force) {
                    this.events.unshift(...batch);
                }
            }
        } catch (error) {
            console.warn('Mouse tracking error:', error);
            // Re-queue events if sending failed
            if (!force) {
                this.events.unshift(...batch);
            }
        }
    }

    // Public method to enable/disable tracking
    setEnabled(enabled) {
        this.enabled = enabled;
        if (enabled && !this.initialized) {
            this.init();
        }
    }

    // Public method to get current tracking status
    getStatus() {
        return {
            enabled: this.enabled,
            sessionId: this.sessionId,
            pendingEvents: this.events.length,
            pendingSessionEvents: this.sessionEvents.length,
            pendingMouseMovements: this.mouseMovements.length,
            viewport: {
                width: this.viewportWidth,
                height: this.viewportHeight
            }
        };
    }

    // Clean up old cached data
    cleanupOldCache() {
        try {
            const keys = Object.keys(localStorage);
            const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours ago

            keys.forEach(key => {
                if (key.startsWith('analytics_')) {
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        if (Array.isArray(data) && data.length > 0) {
                            // Check if the data is old
                            const oldestItem = data[0];
                            if (oldestItem.timestamp && new Date(oldestItem.timestamp).getTime() < cutoffTime) {
                                localStorage.removeItem(key);
                            }
                        }
                    } catch (e) {
                        // If we can't parse it, remove it
                        localStorage.removeItem(key);
                    }
                }
            });
        } catch (error) {
            console.warn('Failed to cleanup old cache:', error);
        }
    }
}

// Auto-initialize if not in admin area and analytics is enabled
document.addEventListener('DOMContentLoaded', function() {
    // Don't track in admin areas or if explicitly disabled
    if (window.location.pathname.startsWith('/admin') ||
        document.querySelector('meta[name="analytics-tracking"][content="disabled"]')) {
        return;
    }

    // Initialize mouse tracker
    window.mouseTracker = new MouseTracker({
        enabled: false,
        endpoint: '/api/analytics/track-mouse'
    });

    // Clean up old cached data every hour
    setInterval(() => {
        if (window.mouseTracker) {
            window.mouseTracker.cleanupOldCache();
        }
    }, 60 * 60 * 1000); // 1 hour
});
